
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, User, Shield, Settings, Bug, Wand2, Link as LinkIcon, Code } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { useAuth } from "@/hooks/useAuth";
import { useUserRoles } from "@/hooks/useUserRoles";
import { TestActions } from "@/components/admin/TestActions";
import { SetupGuideDialog } from "@/components/setup-guide/SetupGuideDialog";
import { useTranslation } from "react-i18next";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useCustomPlanNavigation } from "@/hooks/useCustomPlanNavigation";
import { useSubscriptionAccess } from "@/hooks/useSubscriptionAccess";

export function UserButton() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, logout, isAuthenticated } = useAuth();
  const { useHasRoleQuery } = useUserRoles();
  const { data: isDeveloper } = useHasRoleQuery("DEVELOPER");
  const { data: isAdmin } = useHasRoleQuery("ADMIN");
  const { data: isTester } = useHasRoleQuery("TESTER");
  const [setupGuideOpen, setSetupGuideOpen] = useState(false);
  const { isCustomPlan, featureKeys } = useCustomPlanNavigation();
  const { hasValidPaidSubscription, accessLevel } = useSubscriptionAccess();
  
  // Only show feature-gated items if user has a valid subscription AND has access to the feature
  // Users without valid subscription shouldn't see ERP Integrations or API Portal
  const canAccessFeatures = hasValidPaidSubscription || accessLevel === 'subscriptions_disabled';
  
  const hasErpIntegration = canAccessFeatures && (!isCustomPlan || featureKeys.includes('erp_integration'));
  const hasApiAccess = canAccessFeatures && (!isCustomPlan || featureKeys.includes('api_read') || featureKeys.includes('api_write'));
  const hasContractManagement = canAccessFeatures && (!isCustomPlan || featureKeys.includes('contract_management'));

  if (!isAuthenticated) {
    return (
      <div className="flex items-center gap-2">
        <Button asChild variant="ghost" size="sm">
          <Link to="/login">Log in</Link>
        </Button>
        <Button asChild size="sm">
          <Link to="/register">Sign up</Link>
        </Button>
      </div>
    );
  }

  
  
  const hasAdminAccess = isDeveloper || isAdmin || isTester;
  const isFullAdmin = isDeveloper || isAdmin;

  // Function to navigate and close dropdown
  const handleNavigation = (path: string) => {
    navigate(path);
  };

  return (
    <div className="flex items-center gap-2">
      <TestActions />
      
      {hasAdminAccess && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                asChild
                className="relative h-9 w-9 rounded-full hover:bg-muted"
              >
                <Link to="/admin">
                  {isTester && !isFullAdmin ? (
                    <Bug className="h-[1.2rem] w-[1.2rem] text-foreground" />
                  ) : (
                    <Shield className="h-[1.2rem] w-[1.2rem] text-foreground" />
                  )}
                </Link>
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>{isTester && !isFullAdmin ? t('header.testerArea', 'Tester Area') : t('header.adminPanel', 'Admin Panel')}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
      
      <TooltipProvider>
        <Tooltip>
          <DropdownMenu>
            <TooltipTrigger asChild>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="relative h-9 w-9 rounded-full hover:bg-muted"
                  size="icon"
                >
                  <Avatar className="h-9 w-9">
                    <AvatarFallback className="bg-secondary text-secondary-foreground">
                      {user?.name ? user.name.charAt(0).toUpperCase() : <User size={16} />}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
            </TooltipTrigger>
            <TooltipContent>
              <p>{t('header.userMenu', 'User Menu')}</p>
            </TooltipContent>
          <DropdownMenuContent className="w-56" align="end" forceMount>
            <div className="flex items-center justify-start gap-2 p-2">
              <div className="flex flex-col space-y-0.5 leading-none">
                <p className="font-medium text-sm">{user?.name || "User"}</p>
                {user?.email && (
                  <p className="text-xs text-muted-foreground">{user.email}</p>
                )}
              </div>
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <Link to="/profile" className="flex items-center">
                <User className="mr-2 h-4 w-4" />
                <span>Profile</span>
              </Link>
            </DropdownMenuItem>
            
            {hasAdminAccess && (
              <>
                <DropdownMenuItem asChild>
                  <Link to="/admin" className="flex items-center">
                    {isTester && !isFullAdmin ? (
                      <>
                        <Bug className="mr-2 h-4 w-4" />
                        <span>Tester Area</span>
                      </>
                    ) : (
                      <>
                        <Shield className="mr-2 h-4 w-4" />
                        <span>Admin Panel</span>
                      </>
                    )}
                  </Link>
                </DropdownMenuItem>
              </>
            )}
            
            <DropdownMenuItem asChild>
              <Link to="/settings" className="flex items-center cursor-pointer">
                <Settings className="mr-2 h-4 w-4" />
                <span>Settings</span>
              </Link>
            </DropdownMenuItem>

            {hasErpIntegration && (
              <DropdownMenuItem asChild>
                <Link to="/settings/integrations" className="flex items-center cursor-pointer">
                  <LinkIcon className="mr-2 h-4 w-4" />
                  <span>ERP Integrations</span>
                </Link>
              </DropdownMenuItem>
            )}

            {hasApiAccess && (
              <DropdownMenuItem asChild>
                <Link to="/api-portal" className="flex items-center cursor-pointer">
                  <Code className="mr-2 h-4 w-4" />
                  <span>API Portal</span>
                </Link>
              </DropdownMenuItem>
            )}
            
            {hasContractManagement && (
              <DropdownMenuItem 
                onClick={() => setSetupGuideOpen(true)}
                className="flex items-center cursor-pointer"
              >
                <Wand2 className="mr-2 h-4 w-4" />
                <span>Setup Guide</span>
              </DropdownMenuItem>
            )}
            
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="cursor-pointer text-destructive focus:bg-destructive/10"
              onClick={logout}
            >
              <LogOut className="w-4 h-4 mr-2" />
              <span>Log out</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        </Tooltip>
      </TooltipProvider>
      
      <SetupGuideDialog
        open={setupGuideOpen}
        onOpenChange={setSetupGuideOpen}
      />
    </div>
  );
}
